import { useEffect, useState, useCallback } from "react";
import { Box, Grid2, Typography } from "@mui/material";
import { ThemeProperties } from "../../Theme/ThemeProperties";
import { decryptTheParams } from "../../Helper/QueryParams/EncryptDecrypt";
import ScreenContainer from "../../Components/ScreenContainer";
import {
  analyticsCalendarEndDate,
} from "../../Helper/DatePicker/DateConverter";
import { getConnectedBatteries, getPagesMetaData } from "../../Api/Api";
import { getAvailableTags } from "../../Api/TagsApi";
import AlertSystemTabs from "../../PagesContent/BmsFaults/AlertSystemTabs";
import Events from "../../Analytics/Events";
import CODE from "../../Static/Constants/StatusCodes";
import MultiSelectDropdown from "../../PagesContent/Overview/components/MultiSelectDropdown";
import GlobalFilter from "../../PagesContent/Overview/components/GlobalFilter";
import FilterBar from "../../Components/FilterBar";
import OnboardingLocationFilter from "../../PagesContent/Overview/components/OnboardingLocationFilter";
import AppCache from "../../Helper/Common/AppCache";
import { isFilterVisible } from "../../Helper/Common/pageFilterVisibility";
import OperationalStatusMultiSelect from "../../PagesContent/Common/OperationalStatusMultiSelect";
import { usePermissions } from "../../Context/PermissionContext";
import { FEATURE_CODES } from "../../Static/FeatureMapping";
import ChartCalendarV2 from "../../PagesContent/FleetPerformance/Components/ChartCalendarV2";
import EnergyConsumption from "../../PagesContent/FleetPerformance/EnergyConsumption/EnergyConsumption";
import UtilisationTab from "../../PagesContent/FleetPerformance/Utilization/UtilisationTab";
import RangeTab from "../../PagesContent/FleetPerformance/Range/RangeTab";
import useGlobalFilterData from "../../Hooks/useGlobalFilterData";

const tabs = [
  {
    slug: "energy-consumption",
    name: "Energy Consumption",
    color: ThemeProperties.purple,
  },
  {
    slug: "utilisation",
    name: "Utilisation",
    color: ThemeProperties.purple,
  },
  {
    slug: "range",
    name: "Range",
    color: ThemeProperties.purple,
  },
];


const FleetPerformance = () => {
  const queryParams = decryptTheParams();
  const defaultTabSlug = queryParams.tab
    ? JSON.parse(queryParams.tab)
    : tabs[0]?.slug;
  const { hasPermission } = usePermissions();

  const globalFilterConfig = AppCache.getItem("globalFilter")
    ?.find((f) => f.type === "unifiedGlobalFilter") || null;

  const {
    allFleets,
    selectedFleets,
    allOnboardingLocations,
    allOnboardingLocationsHierarchy,
    globalFilters,
    setGlobalFilters,
    fetchFleets,
    fetchOnboardingLocations,
    handleFleetAndLocationChange: handleGlobalFilterChange,
    canUseGlobalAdvancedFilter,
    filtersLoading,
  } = useGlobalFilterData();

  const [pagesContent, setPagesContent] = useState({ mounted: false });
  const [selectedTab, setSelectedTab] = useState(defaultTabSlug);

  const clientTabs = pagesContent.metaInfo?.tabsToShow
    || AppCache.getItem('fleetPerformanceMeta')?.tabsToShow;
  const visibleTabs = clientTabs?.length
    ? tabs.filter((t) => clientTabs.includes(t.slug))
    : tabs;
  const [allOperationalStatus, setAllOperationalStatus] = useState([]);
  const [operationalStatusOptions, setOperationalStatusOptions] = useState([]);

  // Analytics date range state
  const [analyticsDateRange, setAnalyticsDateRange] = useState(() => {
    const today = new Date();
    const endDate = analyticsCalendarEndDate();

    const startDateObj = new Date();
    startDateObj.setDate(today.getDate() - 7);
    const startDate = startDateObj.toISOString().split('T')[0];

    return {
      startDate,
      endDate,
    };
  });
  const [vehiclesConnected, setVehiclesConnected] = useState(0);
  const [isLoadingVehiclesCount, setIsLoadingVehiclesCount] = useState(false);

  // Energy Consumption chart details state
  const [showChartDetails, setShowChartDetails] = useState(false);
  const [selectedChartType, setSelectedChartType] = useState(null);
  const [selectedChartGranularity, setSelectedChartGranularity] = useState(null);

  const handleShowChartDetails = (show, chartType, granularity) => {
    setShowChartDetails(show);
    setSelectedChartType(chartType);
    setSelectedChartGranularity(granularity);
  };

  // Utilisation trend details state
  const [showTrendDetails, setShowTrendDetails] = useState(false);

  const fetchOperationalStatus = async () => {
    const res = await getAvailableTags();
    const tags = res?.response?.data?.tags || [];

    const statusTag = tags.find(tag => tag.tagCode === 'status');
    const operationalStatusList = statusTag?.supportedValues || [];

    setOperationalStatusOptions(operationalStatusList);

    const operationalStatusValues = operationalStatusList.map(status => status.valueCode);
    const operationalStatusLabels = operationalStatusList.map(status => status.displayName);

    // Default selection: only operational and non_operational (same as BmsFaults)
    const defaultSelection = ["operational", "non_operational"];

    setAllOperationalStatus(operationalStatusLabels);
    setGlobalFilters(prev => ({
      ...prev,
      operationalStatus: defaultSelection,
      allOperationalStatus: operationalStatusValues,
    }));
  };

  const getSelectedOperationalStatusLabels = () => {
    const selectedValues = globalFilters?.operationalStatus || [];
    return selectedValues.map(value => {
      const option = operationalStatusOptions.find(opt => opt.valueCode === value);
      return option ? option.displayName : value;
    });
  };

  const handleOperationalStatusSelection = (selectedLabels) => {
    const selectedValues = selectedLabels.map(label => {
      const option = operationalStatusOptions.find(opt => opt.displayName === label);
      return option ? option.valueCode : label;
    });
    setGlobalFilters(prev => ({
      ...prev,
      operationalStatus: selectedValues,
    }));
  };

  const fetchVehiclesConnected = useCallback(async () => {
    setIsLoadingVehiclesCount(true);
    try {
      const response = await getConnectedBatteries(globalFilters, 'fleetPerformance');

      if (response?.responseStatus?.code === CODE.SUCCESS) {
        setVehiclesConnected(response?.response?.data?.[0]?.count || 0);
      }
    } catch (error) {
      console.error('Error fetching vehicles connected:', error);
      setVehiclesConnected(0);
    } finally {
      setIsLoadingVehiclesCount(false);
    }
  }, [globalFilters]);

  async function getPageMeta() {
    const pageName = "fleetPerformance";
    const res = await getPagesMetaData(pageName);
    Events("fleetPerformance page opened");

    if (res && res?.responseStatus?.code === CODE.SUCCESS) {
      const pageData = res.response?.response?.metaInfo;
      setPagesContent({
        ...pagesContent,
        metaInfo: pageData,
        mounted: true,
      });
    } else {
      // Even if no metadata, mount the page
      setPagesContent({
        ...pagesContent,
        mounted: true,
      });
    }
  }

  useEffect(() => {
    getPageMeta();
  }, []);

  // If the selected tab is not in the visible set (e.g. filtered out by config),
  // snap to the first visible tab.
  useEffect(() => {
    if (visibleTabs.length > 0 && !visibleTabs.find((t) => t.slug === selectedTab)) {
      setSelectedTab(visibleTabs[0].slug);
    }
  }, [visibleTabs]);

  useEffect(() => {
    fetchFleets();
  }, []);

  useEffect(() => {
    fetchOperationalStatus();
  }, []);

  useEffect(() => {
    if (selectedFleets.length > 0) {
      fetchOnboardingLocations(selectedFleets);
    }
  }, [selectedFleets]);

  // Per-page filter visibility, the same flat block BMS Faults and Smart Alerts
  // read. See isFilterVisible for why an absent key means shown: no org has this
  // block today, so the opposite default would blank this header for everyone.
  const pageFiltersConfig = pagesContent?.metaInfo?.globalFilters;
  const showFilter = (key) => isFilterVisible(pageFiltersConfig, key);

  // The org meta arrives after the first render, so the control mounts, seeds a
  // default, and only then gets hidden, leaving a scope the user can neither see
  // nor clear. Unlike the other pages this one has a second seeder in
  // fetchOperationalStatus, which runs on mount and cannot know the config yet,
  // so the current value is a dependency too: whichever seeder wins, what it
  // seeded is cleared. The length guard returns prev unchanged once it is empty,
  // so this settles rather than loops.
  useEffect(() => {
    if (showFilter("operationalStatus")) return;
    setGlobalFilters((prev) =>
      (prev?.operationalStatus || []).length
        ? { ...prev, operationalStatus: [] }
        : prev
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageFiltersConfig, globalFilters?.operationalStatus]);


  // Fetch vehicles connected count
  useEffect(() => {
    if (Object.keys(globalFilters).length > 0) {
      fetchVehiclesConnected();
    }
  }, [globalFilters, analyticsDateRange, fetchVehiclesConnected]);

  const isDetailsActive = showChartDetails || showTrendDetails;

  return (
    <ScreenContainer pagesContent={pagesContent}>
      {!isDetailsActive && (
        <Box
          sx={{
            position: "sticky",
            top: "50px",
            zIndex: 1000,
            backgroundColor: "#FAFAFA",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              pt: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flex: 1,
                justifyContent: "space-between",
                pr: 3,
              }}
            >
              <Box sx={{ px: 3, display: "flex" }}>
                <Typography variant="fw700s20lh24c2E2E2E">
                  Fleet Operational Performance
                </Typography>
              </Box>
              <FilterBar
                testId="fleet-performance-filters"
                filters={[
                  showFilter("dateRange") && {
                    key: "dateRange",
                    node: (
                      <ChartCalendarV2
                        startDate={analyticsDateRange.startDate}
                        endDate={analyticsDateRange.endDate}
                        onDateChange={({ startDate, endDate }) => {
                          setAnalyticsDateRange({ startDate, endDate });
                        }}
                        maxDate={analyticsCalendarEndDate()}
                      />
                    ),
                  },
                  showFilter("operationalStatus") && {
                    key: "operationalStatus",
                    node: (
                      <OperationalStatusMultiSelect
                        selectedValues={globalFilters?.operationalStatus || []}
                        onSelectionChange={(selectedValues) => setGlobalFilters(prev => ({
                          ...prev,
                          operationalStatus: selectedValues,
                        }))}
                        onOptionsLoaded={(allValueCodes, defaultSelection) => setGlobalFilters(prev => ({
                          ...prev,
                          operationalStatus: defaultSelection,
                          allOperationalStatus: allValueCodes,
                        }))}
                      />
                    ),
                  },
                  showFilter("location") && {
                    key: "customerLocation",
                    primary: true,
                    node: canUseGlobalAdvancedFilter && globalFilterConfig ? (
                      <GlobalFilter
                        isLoading={filtersLoading}
                        allFleets={allFleets}
                        allOnboardingLocationsHierarchy={allOnboardingLocationsHierarchy}
                        selectedFleetIds={globalFilters?.fleets || []}
                        selectedLocations={globalFilters?.hierarchicalLocation}
                        onChange={handleGlobalFilterChange}
                      />
                    ) : (
                      <OnboardingLocationFilter
                        locations={allOnboardingLocationsHierarchy}
                        selectedLocations={globalFilters?.hierarchicalLocation}
                        onChange={(selected) => setGlobalFilters(prev => ({ ...prev, hierarchicalLocation: selected }))}
                      />
                    ),
                  },
                ]}
              />
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flex: 1,
              justifyContent: "space-between",
              borderBottom: "1px solid #E4E3E3",
              mt: 0,
              pr: 3,
            }}
          >
            <AlertSystemTabs
              tabs={visibleTabs}
              selectedTab={selectedTab}
              changeTheTab={(tabId) => {
                setSelectedTab(tabId);
              }}
            />
          </Box>
        </Box>
      )}

      {/* Page Content */}
      <Box sx={{ px: 3, py: 2, mb: 2 }}>
        <Grid2 container gap={2} flexDirection={"column"}>

          {selectedTab === "energy-consumption" && (globalFilters?.fleets || []).length > 0 && (
            <EnergyConsumption
              startDate={analyticsDateRange.startDate}
              endDate={analyticsDateRange.endDate}
              vehiclesConnected={vehiclesConnected}
              isLoadingVehiclesCount={isLoadingVehiclesCount}
              globalFilters={globalFilters}
              allFleets={allFleets}
              allOnboardingLocationsHierarchy={allOnboardingLocationsHierarchy}
              showChartDetails={showChartDetails}
              selectedChartType={selectedChartType}
              selectedChartGranularity={selectedChartGranularity}
              onShowDetails={handleShowChartDetails}
            />
          )}

          {selectedTab === "utilisation" && (globalFilters?.fleets || []).length > 0 && (
            <UtilisationTab
              startDate={analyticsDateRange.startDate}
              endDate={analyticsDateRange.endDate}
              vehiclesConnected={vehiclesConnected}
              isLoadingVehiclesCount={isLoadingVehiclesCount}
              globalFilters={globalFilters}
              allOnboardingLocationsHierarchy={allOnboardingLocationsHierarchy}
              showTrendDetails={showTrendDetails}
              onShowTrendDetails={() => setShowTrendDetails(true)}
              onHideTrendDetails={() => setShowTrendDetails(false)}
            />
          )}

          {selectedTab === "range" && (globalFilters?.fleets || []).length > 0 && (
            <RangeTab
              startDate={analyticsDateRange.startDate}
              endDate={analyticsDateRange.endDate}
              vehiclesConnected={vehiclesConnected}
              isLoadingVehiclesCount={isLoadingVehiclesCount}
              globalFilters={globalFilters}
            />
          )}

        </Grid2>
      </Box>
    </ScreenContainer>
  );
};

export default FleetPerformance;
